/**
 * Polling-mode replacement for the SSE bridge.
 *
 * SSE is gone (Workers can't hold Postgres LISTEN). Live-feel now comes from
 * react-query's `refetchInterval` on useInbox / useThread. This hook's job
 * shrinks to one thing: drop optimistic pending bubbles once the polled
 * thread cache shows the confirmed message.
 *
 * Matching strategy:
 *   - tx_hash equality (rare — pending bubbles don't usually have tx_hash)
 *   - content equality: (counterparty_raw, amount_nano, comment.trim())
 *   The pendingStore is the authority for both — we just feed it every
 *   thread cache update.
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { pendingStore } from "../lib/pending";
import { inboxKey } from "./queryKeys";
import type { InboxResponse, ThreadResponse } from "../types/api";

export function useStreamSync(): void {
  const qc = useQueryClient();

  useEffect(() => {
    const unsub = qc.getQueryCache().subscribe((event) => {
      if (event.type !== "updated") return;
      const data = event.query.state.data;
      if (!data || typeof data !== "object") return;
      if (!("messages" in (data as object))) return;
      const td = data as ThreadResponse;
      const counterpartyRaw = td.counterparty_raw;

      // 1. tx_hash match — drops any pending whose hash already showed up.
      pendingStore.reconcileWithThread(td.messages);

      // 2. Content match — drops any pending whose (from, amount, comment)
      //    matches a confirmed outbound message in the thread. Covers the
      //    common case where the pending never got a tx_hash assigned.
      if (counterpartyRaw !== undefined) {
        for (const m of td.messages) {
          if (m.direction === "out") {
            pendingStore.reconcileByContent({
              counterparty_raw: counterpartyRaw,
              amount_nano: m.amount_nano,
              comment: m.comment,
              tx_hash: m.tx_hash,
            });
          }
        }
      }
    });
    return () => unsub();
  }, [qc]);
}

/**
 * Convenience: optimistically bump an inbox row's last_message + unread
 * when the user sends a message and we already know the counterparty.
 * Called from the compose hook so the inbox feels instantaneous on send.
 */
export function bumpInboxRow(
  qc: QueryClient,
  counterpartyRaw: string,
  preview: { comment: string; amount_nano: string },
): void {
  qc.setQueryData<InboxResponse | undefined>(inboxKey(), (prev) => {
    if (!prev) return prev;
    const idx = prev.threads.findIndex(
      (t) => t.counterparty_raw === counterpartyRaw,
    );
    if (idx === -1) return prev;
    const existing = prev.threads[idx];
    if (!existing) return prev;
    const updated = {
      ...existing,
      last_message: {
        ...existing.last_message,
        comment: preview.comment,
        amount_nano: preview.amount_nano,
        direction: "out" as const,
        chain_ts: new Date().toISOString(),
      },
    };
    const next = [updated, ...prev.threads.slice(0, idx), ...prev.threads.slice(idx + 1)];
    return { ...prev, threads: next };
  });
}
